import { useEffect, useMemo, useState } from "react";
import { UserCohort } from "./userCohort";
import { Course } from "./course";
import { DeliverySchedule } from "./deliverySchedule";
import {
  Schedule,
  CourseResult,
  CohortResult,
  CourseResource,
  CohortResource,
} from "@mikros/library";
import { Finish } from "./finish";
import ApiServices from "../../../service/apiService";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Alert } from "@mikros/shared-ui";
import { Stepper, StepperChangeEvent } from "@progress/kendo-react-layout";
import Account from "../../../utils/apiConfig";
import ErrorHandler from "../../../utils/errorConfig";
import { useIntl } from "react-intl";

export interface NewScheduleProps {
}

interface PrefilledCourseState {
  courseResource: CourseResource;
  languageCode: string;
  description: string;
}

interface PrefilledCohortState {
  cohortResource: CohortResource;
}

const parseCourseToScheduleState = (
  rawState: unknown
): PrefilledCourseState | undefined => {
  if (!rawState) {
    return undefined;
  }

  try {
    const parsedState =
      typeof rawState === "string" ? JSON.parse(rawState) : rawState;
    const coursePayload = parsedState?.courseToSchedule;

    if (!coursePayload) {
      return undefined;
    }

    const normalizedPayload = Array.isArray(coursePayload)
      ? coursePayload[0]
      : coursePayload;

    if (!normalizedPayload?.courseId) {
      return undefined;
    }

    const courseResource: CourseResource = {
      entityId: normalizedPayload.entityId ?? "",
      status: normalizedPayload.status ?? "",
      createdAt: normalizedPayload.createdAt ?? "",
      createdBy: normalizedPayload.createdBy ?? "",
      date: normalizedPayload.date ?? "",
      accntId: normalizedPayload.accntId ?? "",
      recType: normalizedPayload.recType ?? "",
      updatedAt: normalizedPayload.updatedAt ?? "",
      courseId: normalizedPayload.courseId,
      description: normalizedPayload.description ?? "",
      entryType: normalizedPayload.entryType ?? "",
      courseName: normalizedPayload.courseName,
      reviewedBy: normalizedPayload.reviewedBy,
      updatedBy: normalizedPayload.updatedBy,
      publishedBy: normalizedPayload.publishedBy,
      reviewedDate: normalizedPayload.reviewedDate,
      publishedDate: normalizedPayload.publishedDate,
      chapterCount: normalizedPayload.chapterCount ?? 0,
      cardCount: normalizedPayload.cardCount ?? 0,
      minAsmtCnt: normalizedPayload.minAsmtCnt ?? 0,
      minCrdCnt: normalizedPayload.minCrdCnt ?? 0,
      maxCardCnt: normalizedPayload.maxCardCnt ?? 0,
    };

    return {
      courseResource,
      description: normalizedPayload.description ?? "",
      languageCode: normalizedPayload.languageCode ?? "en_US",
    };
  } catch (error) {
    console.error("Failed to parse courseToSchedule navigation state", error);
    return undefined;
  }
};

const parseCohortToScheduleState = (
  rawState: unknown
): PrefilledCohortState | undefined => {
  if (!rawState) {
    return undefined;
  }

  try {
    const parsedState =
      typeof rawState === "string" ? JSON.parse(rawState) : rawState;
    const cohortPayload = parsedState?.cohortToSchedule;

    if (!cohortPayload) {
      return undefined;
    }

    const normalizedPayload = Array.isArray(cohortPayload)
      ? cohortPayload[0]
      : cohortPayload;

    if (!normalizedPayload?.cohortId) {
      return undefined;
    }

    const cohortResource: CohortResource = {
      cohortId: normalizedPayload.cohortId,
      entityId: normalizedPayload.entityId ?? "",
      status: normalizedPayload.status ?? "",
      cohortType: normalizedPayload.cohortType ?? "",
      updatedBy: normalizedPayload.updatedBy ?? "",
      cohortuserCnt: normalizedPayload.cohortuserCnt ?? 0,
      createdBy: normalizedPayload.createdBy ?? "",
      date: normalizedPayload.date ?? "",
      cohortName: normalizedPayload.cohortName ?? "",
      accntId: normalizedPayload.accntId ?? "",
      recType: normalizedPayload.recType ?? "",
      description: normalizedPayload.description ?? "",
      entryType: normalizedPayload.entryType ?? "",
    };

    return {
      cohortResource,
    };
  } catch (error) {
    console.error("Failed to parse cohortToSchedule navigation state", error);
    return undefined;
  }
};

export function NewSchedule(props: NewScheduleProps) {
  const accountInstance = Account.getInstance();
  const errorInstance = ErrorHandler.getErrorInstance();
  const intl = useIntl();

  const iconsWithLabel = [
    {
      icon: "k-i-book",
      label: intl.formatMessage({ id: "newSchedule.courseLabel" }),
    },
    {
      icon: "k-i-user",
      label: intl.formatMessage({ id: "newSchedule.userCohorLabel" }),
    },
    {
      icon: "k-i-calendar",
      label: intl.formatMessage({ id: "newSchedule.deliveryScheduleLabel" }),
    },
    {
      icon: "k-i-track-changes",
      label: intl.formatMessage({ id: "newSchedule.finishLabel" }),
    },
  ];

  const initialSheduleState: Schedule = {
    isUserRespReqrd: true,
    isReminderReqrd: false,
    certificateReq: false,

    enrolmntType: "ALL_COHORT_USER",
    isNewUserAllowed: "NEW_USERS_NO",
    endOption: {
      courseEnd: true,
    },
    isSchdlReqrd: true,
    languageCode: "en_US",
  } as Schedule;

  const navigate = useNavigate();
  const location = useLocation();

  const prefilledCourseState = useMemo(
    () => parseCourseToScheduleState(location.state),
    [location.state]
  );
  const prefilledCohortState = useMemo(
    () => parseCohortToScheduleState(location.state),
    [location.state]
  );

  const prefilledCourseResult = useMemo(() => {
    if (!prefilledCourseState) {
      return undefined;
    }
    return {
      resources: [prefilledCourseState.courseResource],
      resourceCount: 1,
    } as CourseResult;
  }, [prefilledCourseState]);

  const prefilledCohortResult = useMemo(() => {
    if (!prefilledCohortState) {
      return undefined;
    }
    return {
      resources: [prefilledCohortState.cohortResource],
      resourceCount: 1,
    } as CohortResult;
  }, [prefilledCohortState]);

  const hasPrefilledCourse = Boolean(prefilledCourseResult);
  const hasPrefilledCohort = Boolean(prefilledCohortResult);

  const [stepperValue, setStepperValue] = useState<number>(0);
  const [schedule, setSchedule] = useState<Schedule>(() => {
    let baseSchedule: Schedule = { ...initialSheduleState };

    if (prefilledCourseState) {
      const courseResource = prefilledCourseState.courseResource;
      baseSchedule = {
        ...baseSchedule,
        courseName: courseResource.courseName,
        languageCode: prefilledCourseState.languageCode,
        description: prefilledCourseState.description,
        courseId: courseResource.courseId,
        accntId: courseResource.accntId,
        cardCnt: courseResource.cardCount,
        chapterCnt: courseResource.chapterCount,
        assessQuesCnt: courseResource.minAsmtCnt,
        minCardCntChpt: courseResource.minCrdCnt,
        maxCardCnt: courseResource.maxCardCnt,
      } as Schedule;
    }

    if (prefilledCohortState) {
      const cohortResource = prefilledCohortState.cohortResource;
      baseSchedule = {
        ...baseSchedule,
        cohortName: cohortResource.cohortName,
        cohortId: cohortResource.cohortId,
        cohortuserCnt: cohortResource.cohortuserCnt,
        enrolmntType: "ALL_COHORT_USER",
      } as Schedule;
    }

    return baseSchedule;
  });

  const [courses, setCourses] = useState<CourseResult>(
    prefilledCourseResult ?? ({} as CourseResult)
  );
  const [cohorts, setCohorts] = useState<CohortResult>(
    prefilledCohortResult ?? ({} as CohortResult)
  );
  const [showDoneDialogue, setShowDoneDialogue] = useState<boolean>(false);
  const [errorDialogue, setErrorDialogue] = useState<boolean>(false);

  //error
  const [errorFlag, setErrorFlag] = useState<boolean>(false);

  console.log("courses print in new schedule", courses);
  console.log("cohorts result", cohorts);

  useEffect(() => {
    console.log("Updated cohorts state:", cohorts);
  }, [cohorts]);


  const handleStepperChange = (event: StepperChangeEvent) => {
    if (event.value < stepperValue) {
      setStepperValue(event.value);
    }
  };

  const stepperChange = (schedule: Schedule) => {
    stepperValue <= 2 ? setStepperValue(stepperValue + 1) : setStepperValue(0);
    setSchedule(schedule);
    console.log(
      "print schedule details in new schedule",
      JSON.stringify(schedule)
    );
  };

  const backStepper = () => {
    setStepperValue(stepperValue - 1);
  };
  console.log("print sched details", schedule);

  const postNewSchedule = (schd: Schedule) => {
    console.log("post schedule details print", schd);
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      api
        .makeRequest(`${accountInstance.accntId}/schedule`, "POST", schd)
        .then(async (data) => {
          if (data && data.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(data.errorMessage);
            return;
          }
          console.log("post schedule", data);
          try {
            if (data.error) {
              setErrorDialogue(true);
            }
          } catch (e) {
            setShowDoneDialogue(true);
          }
        });
    }
  };

  useEffect(() => {
    let api = ApiServices.getInstance();
    async function getCourseAndCohorts() {
      if (accountInstance.accntId) {
        if (!hasPrefilledCourse) {
          await api
            .makeRequest(
              `${accountInstance.accntId}/course/getPublishedCourses`,
              "GET"
            )
            .then(async (data) => {
              if (data && data.errorMessage) {
                setErrorFlag(true);
                await errorInstance.updateErrorCode(data.errorMessage);
                return;
              }
              setCourses(data);
              console.log("print published courses", data?.courseResults);
              console.log("This is the courses in schedules", data);
            })
            .catch((e) => {
              console.log("error on published course fetch", e.errorMessage);
            });
        }

        if (!hasPrefilledCohort) {
          api
            .makeRequest(
              `${accountInstance.accntId}/cohort/getActiveCohorts`,
              "GET"
            )
            .then(async (data) => {
              if (data && data.errorMessage) {
                setErrorFlag(true);
                await errorInstance.updateErrorCode(data.errorMessage);
                return;
              }
              console.log("Filtered cohorts and updated data:", data);
              setCohorts(data);
            });
        }
      }
    }
    getCourseAndCohorts();
  }, [hasPrefilledCourse, hasPrefilledCohort]);

  useEffect(() => {
    async function initializeAndUseAccount() {
      await accountInstance.fetchAccountId();
      console.log(
        "account id instance in tenant side",
        accountInstance.accntId
      );
    }
    initializeAndUseAccount();
  }, []);

  return (
    <div className="w-full h-full text-sm md:text-lg text-mk-very-dark-grey flex flex-col columns-1 justify-start space-y-8 items-start px-8 md:pt-4 ">
      {errorFlag && (
        <Dialog className="errorDialogue">
          <Alert
            description={intl.formatMessage({
              id: errorInstance.errorCode,
            })}
            buttonText={`${intl.formatMessage({
              id: "users.buttonText.Ok",
            })}`}
            icon={"errorShape.png"}
            iconMsg={intl.formatMessage({
              id: "iconMsgError",
            })}
            backgroundColor={"mk-red"}
            onClick={() => {
              setErrorFlag(false);
              if (errorInstance.permDenied === true) {
                navigate("/home");
              } else {
                navigate("/scheduler");
              }
            }}
          />
        </Dialog>
      )}
      {
        showDoneDialogue && (
          <Dialog className="confirmSuccessDialogue">
            <Alert
              buttonText={intl.formatMessage({ id: "users.buttonText.Ok" })}
              description={intl.formatMessage({
                id: "newSchedule.scheduleCreationSuccess",
              })}
              icon={"successShape.png"}
              iconMsg={intl.formatMessage({ id: "iconMsgSuccess" })}
              backgroundColor={"mk-bright-green"}
              onClick={() => {
                setShowDoneDialogue(false);
                navigate("/scheduler");
              }}
            />
          </Dialog>
        )
      }
      {errorDialogue && (
        <Dialog className="confirmSuccessDialogue">
          <Alert
            buttonText={intl.formatMessage({ id: "newSchedule.recheck" })}
            description={intl.formatMessage({ id: "newSchedule.scheduleCreationFailure" })}
            icon={"successShape.png"}
            iconMsg={intl.formatMessage({ id: "iconMsgSuccess" })}
            backgroundColor={"mk-bright-green"}
            onClick={() => {
              setErrorDialogue(false);
            }}
          />
        </Dialog>
      )}
      <div className="pt-6 pb-2 md:pt-8 flex justify-center items-center w-full">
        <Stepper
          value={stepperValue}
          className="w-1/2"
          onChange={handleStepperChange}
          items={iconsWithLabel}
        />
     
      </div>
      {(() => {
        switch (stepperValue) {
          case 1:
            return (
              <UserCohort
                backStepper={backStepper}
                onStepperChange={(schedule) => stepperChange(schedule)}
                schedule={schedule}
                cohortResult={cohorts}
              />
            );
          case 2:
            return (
              <DeliverySchedule
                backStepper={backStepper}
                onStepperChange={(schedule) => stepperChange(schedule)}
                schedule={schedule}
              />
            );
          case 3:
            return (
              <Finish
                backStepper={backStepper}
                postNewSchedule={() => postNewSchedule(schedule)}
                schedule={schedule}
              />
            );
          default:
            return (
              <Course
                onStepperChange={(schedule) => stepperChange(schedule)}
                schedule={schedule}
                courseResults={courses}
              />
            );
        }
      })()}
    </div>
  );
}
